import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import QRCode from "qrcode";
import {
  Video, ShieldCheck, ArrowLeft, Loader2, Check, Gift, Send, Printer, RefreshCw,
  Smartphone, KeyRound, EyeOff, Ticket, Copy, Download, Plus, Ban, Bell, BellRing, AlertTriangle, Share, MoreVertical, Settings,
} from "lucide-react";
import { API } from "../App";
import { initAdminAppOneSignal, requestPushPermission, getPushPermission } from "../lib/onesignal";

const BLUE = "#2F6FE0", INK = "#16130F", MUTED = "#6B7280", LINE = "#E6E9EF", CREAM = "#FFF8EC";

const MODE_OPTS = [
  { id: "nessuna", label: "Nessuna identificazione", icon: EyeOff, desc: "L'ospite entra senza verifica" },
  { id: "spioncino", label: "Spioncino", icon: KeyRound, desc: "L'ospite conferma da solo l'arrivo" },
  { id: "devisu", label: "App DeVisu", icon: Video, desc: "Videochiamata con l'host" },
];

export default function DevisuAdmin() {
  const nav = useNavigate();
  const token = localStorage.getItem("token");
  const bearer = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [props, setProps] = useState([]);
  const [busy, setBusy] = useState("");
  const [promo, setPromo] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState({ durata: "1mese", usi_massimi: 1, nota: "" });
  const [pushPerm, setPushPerm] = useState("default");
  const [pushBusy, setPushBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/host/devisu`, { headers: bearer }),
        axios.get(`${API}/properties`, { headers: bearer }),
      ]);
      setState(s.data);
      const list = Array.isArray(p.data) ? p.data : (p.data.properties || p.data.items || []);
      setProps(list);
      // super admin? prova a leggere i codici
      try {
        const c = await axios.get(`${API}/devisu/admin/codes`, { headers: bearer });
        setIsAdmin(true); setCodes(c.data || []);
      } catch { setIsAdmin(false); }
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // OneSignal per l'app host (così la push DeVisu può raggiungere questo dispositivo)
  useEffect(() => {
    setPushPerm(getPushPermission());
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`, { headers: bearer });
        if (me.data?.user_id) await initAdminAppOneSignal(me.data, token);
        setPushPerm(getPushPermission());
      } catch (_) {}
    })();
  }, []); // eslint-disable-line

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const r = await requestPushPermission(null, { registerEndpoint: "/api/admin-app/push/register", authToken: token });
      setPushPerm(getPushPermission());
      if (r?.granted) toast.success("Notifiche attivate su questo dispositivo!");
      else toast.error("Permesso notifiche non concesso. Abilitalo dalle impostazioni del browser.");
    } catch { toast.error("Errore"); } finally { setPushBusy(false); }
  };

  // post-redirect Stripe: conferma pagamento DeVisu
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("devisu_paid") === "1" && sp.get("session_id")) {
      axios.post(`${API}/host/devisu/confirm`, { session_id: sp.get("session_id") }, { headers: bearer })
        .then((r) => { if (r.data.activated) { toast.success("Abbonamento DeVisu attivato! 🎉"); load(); } })
        .catch(() => {})
        .finally(() => window.history.replaceState({}, "", "/dashboard/devisu"));
    }
  }, []); // eslint-disable-line

  const checkout = async () => {
    setBusy("checkout");
    try {
      const r = await axios.post(`${API}/host/devisu/checkout`, { origin_url: window.location.origin }, { headers: bearer });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Errore pagamento"); setBusy(""); }
  };

  const startTrial = async () => {
    setBusy("trial");
    try { await axios.post(`${API}/host/devisu/trial`, {}, { headers: bearer }); toast.success("Prova gratuita di 7 giorni attivata!"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(""); }
  };
  const redeem = async () => {
    if (!promo.trim()) return;
    setBusy("redeem");
    try {
      const r = await axios.post(`${API}/host/devisu/redeem`, { codice: promo.trim() }, { headers: bearer });
      toast.success(r.data.vita ? "Codice a vita attivato! 🎉" : `Codice attivato fino al ${new Date(r.data.paid_until).toLocaleDateString("it-IT")}`);
      setPromo(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Codice non valido"); } finally { setBusy(""); }
  };
  const setMode = async (pid, mode) => {
    setBusy(`mode-${pid}`);
    try { await axios.put(`${API}/host/devisu/mode`, { property_id: pid, mode }, { headers: bearer }); setState((s) => ({ ...s, modes: { ...s.modes, [pid]: mode } })); toast.success("Impostazione salvata"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(""); }
  };
  const genCode = async () => {
    setBusy("gen");
    try {
      await axios.post(`${API}/devisu/admin/codes`, newCode, { headers: bearer });
      toast.success("Codice generato!");
      const c = await axios.get(`${API}/devisu/admin/codes`, { headers: bearer }); setCodes(c.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(""); }
  };
  const revokeCode = async (code) => {
    if (!window.confirm(`Revocare il codice ${code}?`)) return;
    try { await axios.post(`${API}/devisu/admin/codes/${code}/revoca`, {}, { headers: bearer }); const c = await axios.get(`${API}/devisu/admin/codes`, { headers: bearer }); setCodes(c.data || []); toast.success("Codice revocato"); }
    catch { toast.error("Errore"); }
  };
  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/host/devisu/logs?csv=1`, { headers: bearer, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "devisu_log.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore export"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}><Loader2 className="w-7 h-7 animate-spin" style={{ color: BLUE }} /></div>;

  const st = state || {};
  const card = { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 24, padding: 20 };

  return (
    <div className="min-h-screen pb-20" style={{ background: CREAM, color: INK }} data-testid="devisu-admin">
      <div className="max-w-3xl mx-auto px-4 pt-5">
        <button onClick={() => nav("/dashboard")} className="inline-flex items-center gap-1.5 text-sm font-bold mb-4" style={{ color: MUTED }} data-testid="devisu-back"><ArrowLeft className="w-4 h-4" /> Dashboard</button>
        <div className="flex items-center gap-3 mb-5">
          <span className="w-12 h-12 rounded-2xl flex items-center justify-center text-white" style={{ background: BLUE }}><Video className="w-6 h-6" /></span>
          <div>
            <h1 className="text-2xl font-black leading-none">DeVisu</h1>
            <p className="text-sm" style={{ color: MUTED }}>Identificazione dell'ospite in videochiamata</p>
          </div>
        </div>

        {!st.self_checkin_active && (
          <div style={card} className="mb-4 flex items-center gap-3" data-testid="devisu-need-checkin">
            <ShieldCheck className="w-5 h-5 shrink-0" style={{ color: "#D7362B" }} />
            <p className="text-sm font-bold">Attiva prima il <button className="underline" onClick={() => nav("/plugin/self-checkin")}>Self check-in</button> per usare DeVisu.</p>
          </div>
        )}

        {/* ABBONAMENTO */}
        <div style={card} className="mb-4" data-testid="devisu-subscription">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>Stato abbonamento</p>
              {st.status === "attivo" && <p className="text-lg font-black" style={{ color: "#2E9E57" }}>Attivo{st.paid_until && new Date(st.paid_until).getFullYear() < 2090 ? ` · fino al ${new Date(st.paid_until).toLocaleDateString("it-IT")}` : st.paid_until ? " · a vita" : ""}</p>}
              {st.status === "prova" && <p className="text-lg font-black" style={{ color: BLUE }}>Prova gratuita · mancano {st.trial_days_left} giorni</p>}
              {st.status === "scaduto" && <p className="text-lg font-black" style={{ color: "#D7362B" }}>Prova scaduta</p>}
              {st.status === "mai" && <p className="text-lg font-black">Non attivo</p>}
            </div>
            {st.status === "mai" && st.self_checkin_active && (
              <button onClick={startTrial} disabled={busy === "trial"} className="rounded-2xl px-5 py-3 font-black text-white text-sm inline-flex items-center gap-2" style={{ background: BLUE }} data-testid="devisu-start-trial">
                {busy === "trial" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} Prova gratis 7 giorni
              </button>
            )}
          </div>
          {(st.status === "prova" || st.status === "scaduto") && (
            <div className="mt-3 rounded-2xl p-3 flex items-center justify-between flex-wrap gap-2" style={{ background: "#EEF3FC" }}>
              <p className="text-sm font-bold">Attiva DeVisu per tutte le tue strutture a <b>29,90 €/anno</b> (IVA inclusa)</p>
              <button onClick={checkout} disabled={busy === "checkout"} className="rounded-xl px-4 py-2.5 font-black text-white text-sm inline-flex items-center gap-1.5" style={{ background: BLUE }} data-testid="devisu-checkout">
                {busy === "checkout" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Attiva a 29,90 €/anno
              </button>
            </div>
          )}
          {/* Codice promo */}
          {st.self_checkin_active && (
            <div className="mt-3">
              <p className="text-xs font-bold mb-1.5" style={{ color: MUTED }}>Hai un codice promo?</p>
              <div className="flex gap-2">
                <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="DV-XXXX-XXXX" className="flex-1 rounded-xl px-3 py-2.5 text-sm font-bold outline-none" style={{ border: `1px solid ${LINE}` }} data-testid="devisu-promo-input" />
                <button onClick={redeem} disabled={busy === "redeem"} className="rounded-xl px-4 py-2.5 font-bold text-white text-sm inline-flex items-center gap-1.5" style={{ background: INK }} data-testid="devisu-promo-redeem">{busy === "redeem" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />} Riscatta</button>
              </div>
            </div>
          )}
        </div>

        {/* QR DELLE PORTE PER STRUTTURA (solo quando DeVisu è attivo) */}
        {st.self_checkin_active && (st.status === "prova" || st.status === "attivo") && (
          <div style={card} className="mb-4" data-testid="devisu-doors">
            <p className="font-black text-base mb-1">QR delle porte</p>
            <p className="text-xs mb-4" style={{ color: MUTED }}>Stampa il QR e attaccalo alla porta di ogni struttura: l'ospite lo inquadra per avviare l'identificazione in videochiamata. Il tipo di identificazione si sceglie dalle impostazioni di ogni struttura.</p>
            <div className="space-y-4">
              {props.map((p) => (
                <div key={p.property_id} className="rounded-2xl p-3" style={{ border: `1px solid ${LINE}` }} data-testid={`devisu-prop-${p.property_id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="font-bold text-sm">{p.name}</p>
                    <button onClick={() => nav(`/dashboard/property/${p.property_id}/checkin-documenti`)} className="text-xs font-bold inline-flex items-center gap-1" style={{ color: BLUE }} data-testid={`devisu-settings-${p.property_id}`}>
                      <Settings className="w-3.5 h-3.5" /> Impostazioni identificazione
                    </button>
                  </div>
                  <DoorQR pid={p.property_id} bearer={bearer} />
                </div>
              ))}
              {props.length === 0 && <p className="text-sm" style={{ color: MUTED }}>Nessuna struttura trovata.</p>}
            </div>
          </div>
        )}

        {/* AVVISO OBBLIGATORIO: canale di notifica */}
        {st.self_checkin_active && (
          <div style={{ ...card, borderColor: "#F3C98B", background: "#FFFBF3" }} className="mb-4" data-testid="devisu-notify-required">
            <p className="font-black text-base flex items-center gap-2"><AlertTriangle className="w-5 h-5" style={{ color: "#E08A1E" }} /> Attiva un avviso (obbligatorio)</p>
            <p className="text-sm mt-1.5" style={{ color: MUTED }}>
              Quando un ospite chiede l'identificazione, la videochiamata parte subito: per rispondere in tempo <b>devi</b> ricevere l'avviso. Attiva <b>almeno uno</b> di questi canali:
            </p>

            {/* 1) Telegram */}
            <div className="mt-3 rounded-2xl p-3 flex items-center gap-3" style={{ border: `1px solid ${LINE}`, background: "#fff" }}>
              <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: "#229ED9" }}><Send className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">Avvisi Telegram</p>
                <p className="text-[12px]" style={{ color: st.telegram_linked ? "#2E9E57" : MUTED }}>{st.telegram_linked ? "Collegato ✓" : "Non collegato"}</p>
              </div>
              {st.telegram_linked
                ? <Check className="w-5 h-5" style={{ color: "#2E9E57" }} />
                : <a href={`https://t.me/${st.telegram_bot}`} target="_blank" rel="noopener noreferrer" className="rounded-xl px-3 py-2 font-bold text-white text-xs" style={{ background: "#229ED9" }} data-testid="devisu-notify-telegram">Collega</a>}
            </div>

            {/* 2) App Hostland Control + push */}
            <div className="mt-2.5 rounded-2xl p-3 flex items-center gap-3" style={{ border: `1px solid ${LINE}`, background: "#fff" }}>
              <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: BLUE }}><BellRing className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">Notifiche push · app Hostland Control</p>
                <p className="text-[12px]" style={{ color: pushPerm === "granted" ? "#2E9E57" : MUTED }}>
                  {pushPerm === "granted" ? "Attive su questo dispositivo ✓" : pushPerm === "denied" ? "Bloccate dal browser" : pushPerm === "unsupported" ? "Non supportate da questo browser" : "Non attive su questo dispositivo"}
                </p>
              </div>
              {pushPerm === "granted"
                ? <Check className="w-5 h-5" style={{ color: "#2E9E57" }} />
                : <button onClick={enablePush} disabled={pushBusy || pushPerm === "unsupported"} className="rounded-xl px-3 py-2 font-bold text-white text-xs inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: BLUE }} data-testid="devisu-notify-push">{pushBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />} Attiva</button>}
            </div>

            {/* Come installare Hostland Control */}
            <div className="mt-3 rounded-2xl p-3" style={{ background: "#F4F7FC" }} data-testid="devisu-install-guide">
              <p className="font-bold text-[13px] mb-1.5 flex items-center gap-1.5"><Smartphone className="w-4 h-4" style={{ color: BLUE }} /> Come installare l'app Hostland Control</p>
              <ol className="text-[12.5px] space-y-1" style={{ color: MUTED, listStyle: "decimal", paddingLeft: 18 }}>
                <li>Apri <button onClick={() => nav("/control")} className="font-bold underline" style={{ color: BLUE }} data-testid="devisu-open-control">hostland…/control</button> sul telefono.</li>
                <li><b>iPhone (Safari):</b> tocca <Share className="w-3.5 h-3.5 inline" /> Condividi → <b>“Aggiungi a Home”</b>.</li>
                <li><b>Android (Chrome):</b> tocca <MoreVertical className="w-3.5 h-3.5 inline" /> menu → <b>“Installa app”</b>.</li>
                <li>Apri l'app installata e consenti le <b>notifiche</b> quando richiesto.</li>
              </ol>
            </div>
          </div>
        )}

        {/* REGISTRO IDENTIFICAZIONI */}
        {st.self_checkin_active && <DevisuLogs bearer={bearer} onCsv={downloadCsv} />}
      </div>
    </div>
  );
}

// ── QR della porta per una struttura (export ad alta risoluzione 2000px) ──
function DoorQR({ pid, bearer }) {
  const [door, setDoor] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/host/devisu/doors/${pid}`, { headers: bearer }).then((r) => setDoor(r.data)).catch(() => {}); }, [pid]); // eslint-disable-line
  const rotate = async () => {
    if (!window.confirm("Rigenerare il QR? Il precedente non funzionerà più.")) return;
    setBusy(true);
    try { const r = await axios.post(`${API}/host/devisu/doors/${pid}/rotate`, {}, { headers: bearer }); setDoor(r.data); toast.success("QR rigenerato"); }
    catch { toast.error("Errore"); } finally { setBusy(false); }
  };
  const buildHiRes = () => QRCode.toDataURL(door.url, { width: 2000, margin: 2, errorCorrectionLevel: "M", color: { dark: "#16130F", light: "#FFFFFF" } });
  const scaricaQr = async () => {
    try {
      const url = await buildHiRes();
      const a = document.createElement("a");
      a.href = url; a.download = `devisu-qr-${door.short_code}.png`; a.click();
    } catch { toast.error("Errore"); }
  };
  const stampaPdf = async () => {
    if (!door) return;
    let img = "";
    try { img = await buildHiRes(); } catch { toast.error("Errore"); return; }
    const logo = `${window.location.origin}/hostland-logo-trasparente.png`;
    const w = window.open("", "_blank"); if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>DeVisu · ${door.short_code}</title>
<style>
@page{size:A5;margin:0}
*{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{font-family:-apple-system,Segoe UI,Roboto,system-ui,sans-serif;background:#FFF8EC;color:#16130F}
.sheet{width:148mm;height:210mm;display:flex;flex-direction:column;align-items:center;padding:14mm 12mm;position:relative}
.logo{height:11mm;margin-bottom:2mm}
.kick{font-size:9pt;letter-spacing:4px;font-weight:800;color:#2F6FE0;text-transform:uppercase;margin-bottom:3mm}
h1{font-size:26pt;font-weight:800;line-height:1.05;text-align:center;letter-spacing:-.5px}
.sub{font-size:11pt;color:#5B5346;text-align:center;margin-top:3mm;max-width:118mm;line-height:1.45}
.qrwrap{margin:7mm 0 4mm;background:#fff;border:2px solid #16130F;border-radius:6mm;padding:6mm;box-shadow:0 10mm 20mm -10mm rgba(0,0,0,.25)}
.qrwrap img{width:70mm;height:70mm;display:block}
.code{font-size:30pt;font-weight:800;letter-spacing:8px;margin:1mm 0 2mm}
.codelbl{font-size:9pt;color:#8A8172;text-transform:uppercase;letter-spacing:2px;font-weight:700}
.steps{margin-top:5mm;font-size:10pt;color:#3C372E;text-align:center;line-height:1.5;max-width:120mm}
.foot{position:absolute;bottom:10mm;left:0;right:0;text-align:center}
.foot .site{font-size:11pt;font-weight:800;color:#16130F}
.foot .tag{font-size:8.5pt;color:#8A8172;margin-top:1mm}
.badge{display:inline-block;background:#2F6FE0;color:#fff;font-size:8.5pt;font-weight:800;padding:1.5mm 4mm;border-radius:999px;letter-spacing:.5px;margin-bottom:3mm}
</style></head><body>
<div class="sheet">
  <img class="logo" src="${logo}" alt="Hostland"/>
  <div class="kick">Hostland · DeVisu</div>
  <span class="badge">IDENTIFICAZIONE ALL'ARRIVO</span>
  <h1>Inquadra il QR<br/>per entrare</h1>
  <p class="sub">Apri il tuo <b>Welcome Book</b>, tocca <b>“Identificazione in videochiamata”</b> e inquadra questo codice. Scan this QR from your Welcome Book to start the video identification.</p>
  <div class="qrwrap"><img src="${img}" alt="QR DeVisu"/></div>
  <div class="codelbl">Codice porta</div>
  <div class="code">${door.short_code}</div>
  <div class="foot">
    <div class="site">hostland.vitadahost.it</div>
    <div class="tag">Videochiamata dal vivo · de visu, in tempo reale · a norma di legge</div>
  </div>
</div>
<script>var i=new Image();i.src="${logo}";window.onload=function(){setTimeout(function(){window.print()},500)}</script>
</body></html>`);
    w.document.close();
  };
  if (!door) return null;
  return (
    <div className="mt-3 rounded-2xl p-3 flex items-center gap-3 flex-wrap" style={{ background: CREAM, border: `1px solid ${LINE}` }} data-testid={`devisu-door-${pid}`}>
      {/* QR visibile (piccolo) + QR nascosto ad alta risoluzione per l'export */}
      <QRCodeCanvas id={`qr-${pid}`} value={door.url} size={84} bgColor="#FFF8EC" fgColor="#16130F" />
      <div className="flex-1 min-w-[120px]">
        <p className="text-[11px] font-bold" style={{ color: MUTED }}>Codice porta</p>
        <p className="text-xl font-black tracking-widest">{door.short_code}</p>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={scaricaQr} className="rounded-xl px-3 py-2 font-bold text-white text-xs inline-flex items-center gap-1.5" style={{ background: BLUE }} data-testid={`devisu-door-qr-${pid}`}><Download className="w-3.5 h-3.5" /> Scarica QR</button>
        <button onClick={stampaPdf} className="rounded-xl px-3 py-2 font-bold text-white text-xs inline-flex items-center gap-1.5" style={{ background: INK }} data-testid={`devisu-door-print-${pid}`}><Printer className="w-3.5 h-3.5" /> PDF A5</button>
        <button onClick={rotate} disabled={busy} className="rounded-xl px-3 py-2 font-bold text-xs inline-flex items-center gap-1.5" style={{ border: `1px solid ${LINE}` }} data-testid={`devisu-door-rotate-${pid}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Rigenera</button>
      </div>
    </div>
  );
}

// ── Registro identificazioni ──
function DevisuLogs({ bearer, onCsv }) {
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/host/devisu/logs`, { headers: bearer }).then((r) => setItems(r.data.items || [])).catch(() => {}); }, []); // eslint-disable-line
  const esitoLabel = { identificato: "Identificato", approvato_senza_risposta: "Approvato senza risposta", annullato: "Annullato" };
  const esitoColor = { identificato: "#2E9E57", approvato_senza_risposta: "#F08A24", annullato: "#D7362B" };
  return (
    <div style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 24, padding: 20 }} className="mb-4" data-testid="devisu-logs">
      <div className="flex items-center justify-between mb-3">
        <p className="font-black text-base">Registro identificazioni</p>
        <button onClick={onCsv} className="text-xs font-bold inline-flex items-center gap-1.5 rounded-xl px-3 py-2" style={{ border: `1px solid ${LINE}` }} data-testid="devisu-logs-csv"><Download className="w-3.5 h-3.5" /> CSV</button>
      </div>
      {items.length === 0 ? <p className="text-sm" style={{ color: MUTED }}>Nessuna identificazione registrata.</p> : (
        <div className="space-y-2">
          {items.map((i) => (
            <div key={i.id} className="rounded-xl p-2.5 flex items-center gap-2 flex-wrap" style={{ background: "#F7F8FA" }}>
              <span className="text-xs font-bold">{new Date(i.data).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
              <span className="text-xs" style={{ color: MUTED }}>{i.struttura} · {i.ospite}</span>
              <span className="ml-auto text-[11px] font-black px-2 py-0.5 rounded-full text-white" style={{ background: esitoColor[i.esito] || MUTED }}>{esitoLabel[i.esito] || i.esito}</span>
              {i.durata_s != null && <span className="text-[11px]" style={{ color: MUTED }}>{i.durata_s}s</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
